import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { Order, CartItem } from '../types';
import { doc, writeBatch, arrayUnion, getDoc } from 'firebase/firestore';
import { deleteOnlinePayment } from '../utils/onlinePayment';
import { db } from '../firebase/config';
import { processReferralCommission } from '../utils/referralCommission';

import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowRightIcon,
  HomeIcon,
  BookOpenIcon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getCourseDiscordInvite } from '../utils/discord';
import axios from 'axios';
import AIChatButton from '../components/AIChatButton';
import { shouldShowIOSWebViewAlert } from '../utils/deviceDetection';

const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { clearCart } = useCart();
  const [paymentStatus, setPaymentStatus] = useState<'success' | 'failed' | 'pending' | 'cancelled' | 'unknown'>('unknown');
  const [orderData, setOrderData] = useState<Order | null>(null);
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [isInIframe, setIsInIframe] = useState(false);
  const [iframePaymentData, setIframePaymentData] = useState<any>(null);
  const hasProcessedPaymentRef = useRef(false);

  // Detect if the component is running in an iframe
  useEffect(() => {
    try {
      setIsInIframe(window.self !== window.top);
      console.log('PaymentSuccess - Iframe detection:', window.self !== window.top);
    } catch (error) {
      // If we can't access window.top, we're likely in an iframe
      setIsInIframe(true);
      console.log('PaymentSuccess - Iframe detected via error handling');
    }
  }, []);

  // Function to send message to parent window (for iframe scenarios)
  const sendMessageToParent = useCallback((message: any) => {
    if (!isInIframe) return;
    try {
      window.parent.postMessage(message, '*');
      console.log('PaymentSuccess - Message sent to parent:', message);
    } catch (error) {
      console.error('PaymentSuccess - Failed to send message to parent:', error);
    }
  }, [isInIframe]);

  // Function to save order to database and update user enrollments
  const saveOrderToDatabase = useCallback(async (order: Order) => {
    try {
      console.log('PaymentSuccess - Saving order to database:', order);
      
      const batch = writeBatch(db);
      // Refs
      const userOrderRef = doc(db, 'users', order.userId, 'orders', order.id);
      const mainOrderRef = doc(db, 'orders', order.id);
      const userRef = doc(db, 'users', order.userId);

      // Ensure user doc exists (merge)
      batch.set(userRef, { updatedAt: new Date() }, { merge: true });

      // Orders
      batch.set(userOrderRef, order);
      batch.set(mainOrderRef, order);

      // Courses
      const courseItems = order.items.filter(item => item.type === 'course');
      if (courseItems.length > 0) {
        for (const courseItem of courseItems) {
          const expireDate = new Date();
          expireDate.setMonth(expireDate.getMonth() + 3);

          const courseEnrollmentRef = doc(db, 'users', order.userId, 'Courses', courseItem.itemId);
          const enrollmentData = {
            course_id: courseItem.itemId,
            expire_date: expireDate.toISOString(),
            order_id: order.id,
            payment_reference: order.paymentReference,
            transaction_id: order.transactionId || order.paymentReference || order.id,
            course_name: courseItem.name,
            course_type: courseItem.type,
            timestamp: new Date().toISOString()
          };
          batch.set(courseEnrollmentRef, enrollmentData);
        }

        const courseIds = courseItems.map(item => item.itemId);
        batch.update(userRef, {
          enrolledCourses: arrayUnion(...courseIds),
          updatedAt: new Date()
        });
      }

      // Books
      const bookItems = order.items.filter(item => item.type === 'book');
      if (bookItems.length > 0) {
        for (const bookItem of bookItems) {
          const bookPurchaseRef = doc(db, 'Mybooks', order.userId, 'books', bookItem.itemId);
          const purchaseData = {
            bookid: bookItem.itemId,
            order_id: order.id,
            payment_reference: order.paymentReference,
            transaction_id: order.transactionId || order.paymentReference || order.id,
            course_name: bookItem.name,
            course_type: bookItem.type,
            timestamp: new Date().toISOString()
          };
          batch.set(bookPurchaseRef, purchaseData);
        }

        const bookIds = bookItems.map(item => item.itemId);
        batch.update(userRef, {
          purchasedBooks: arrayUnion(...bookIds),
          updatedAt: new Date()
        });
      }

      // Commit all writes once
      await batch.commit();
      console.log('PaymentSuccess - Batched database operations committed successfully');
      
      // Process referral commission if applicable - only after payment is successful
      if (order.referredBy) {
        try {
          console.log('PaymentSuccess - Processing referral commission for referrer:', order.referredBy);
          
          // Convert Order items to PaymentItem format for commission calculation
          const paymentItems = order.items.map(item => ({
            type: item.type,
            id: item.id,
            name: item.name,
            amount: item.amount
          }));
          
          // Process the referral commission
          const commissionId = await processReferralCommission(
            order.referredBy,
            order.id,
            order.paymentReference || order.transactionId || order.id,
            paymentItems,
            order.total
          );
          
          console.log('PaymentSuccess - Referral commission processed successfully. Commission ID:', commissionId);
        } catch (error) {
          console.error('PaymentSuccess - Error processing referral commission:', error);
          // Don't fail the order save if commission processing fails
          // The order is still valid even if commission processing fails
        }
      } else {
        console.log('PaymentSuccess - No referral commission to process (user was not referred)');
      }
      
      console.log('PaymentSuccess - Database operations completed successfully');
      try {
        // Automated Discord redirection and email confirmation
        const courseItems = order.items.filter(item => item.type === 'course');
        let discordUrl: string | null = null;
        if (courseItems.length > 0) {
          // Prefer first course discord link; could be extended to show chooser
          for (const ci of courseItems) {
            const url = await getCourseDiscordInvite(ci.itemId);
            if (url) { discordUrl = url; break; }
          }
        }

        // Prefer explicit base; fallback to deployed Cloud Functions domain to avoid CORS/local issues
        const functionsBase = (process.env.REACT_APP_FUNCTIONS_BASE_URL || `https://us-central1-profstudymate.cloudfunctions.net/pdfExtractor`).replace(/\/$/, '');

        // Build work plan content from the purchased course/book description
        let workPlan = '';
        try {
          let description: string | null = null;

          // Try to get a course description first (prefer courses if present)
          if (courseItems.length > 0) {
            for (const ci of courseItems) {
              const snap = await getDoc(doc(db, 'courses', ci.itemId));
              if (snap.exists()) {
                const data: any = snap.data();
                const desc = (data?.description || data?.overview || '').toString().trim();
                if (desc) { description = desc; break; }
              }
            }
          }

          // If no course description found, try books
          if (!description) {
            const bookItemsLocal = order.items.filter(item => item.type === 'book');
            for (const bi of bookItemsLocal) {
              const snap = await getDoc(doc(db, 'books', bi.itemId));
              if (snap.exists()) {
                const data: any = snap.data();
                const desc = (data?.description || data?.overview || '').toString().trim();
                if (desc) { description = desc; break; }
              }
            }
          }

          workPlan = description || '';
        } catch (e) {
          console.warn('PaymentSuccess - Unable to build work plan from item description', e);
        }
        try {
          await axios.post(`${functionsBase}/send-payment-confirmation`, {
            toEmail: currentUser?.email || '',
            studentName: (order.shippingAddress ? `${order.shippingAddress.firstName} ${order.shippingAddress.lastName}` : (currentUser?.name || 'Student')).trim(),
            courseTitles: courseItems.map(ci => ci.name),
            discordUrl,
            workPlan,
          });
        } catch (e) {
          console.warn('Email confirmation failed', e);
        }

        // Open Discord invite if available
        if (discordUrl) {
          window.open(discordUrl, '_blank');
        }
      } catch (e) {
        console.warn('Post-payment follow-up failed', e);
      }
      return true;
    } catch (error) {
      console.error('PaymentSuccess - Error saving order to database:', error);
      return false;
    }
  }, [currentUser]);

  const verifyPayment = useCallback(async () => {
    if (!currentUser) {
      setVerificationError('User not authenticated');
      setIsVerifying(false);
      return;
    }

    // Prevent repeated verification and Firestore writes
    if (hasProcessedPaymentRef.current) {
      console.log('PaymentSuccess - Verification already processed, skipping.');
      return;
    }
    hasProcessedPaymentRef.current = true;

    try {
      setIsVerifying(true);
      
      // Check URL parameters first for direct payment success
      const urlParams = new URLSearchParams(window.location.search);
      const status = urlParams.get('status');
      const txRef = urlParams.get('tx_ref');
      const transactionId = urlParams.get('transaction_id');
      
      console.log('PaymentSuccess - URL Parameters:', { status, txRef, transactionId });
      console.log('PaymentSuccess - Is in iframe:', isInIframe);
      
      
      // For iframe scenarios, also check for postMessage data
      if (isInIframe) {
        // Listen for postMessage from parent window
        const handleMessage = (event: MessageEvent) => {
          if (event.data && event.data.type === 'PAYMENT_SUCCESS') {
            setIframePaymentData(event.data.paymentData);
            console.log('PaymentSuccess - Received payment data via postMessage:', event.data.paymentData);
          }
        };
        
        window.addEventListener('message', handleMessage);
        
        // Request payment data from parent window
        sendMessageToParent({ type: 'REQUEST_PAYMENT_DATA' });
        
        // Wait a bit for response
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        window.removeEventListener('message', handleMessage);
        
        // Also check if we have payment data stored in parent's localStorage (accessible via postMessage)
        try {
          // Try to get payment data from parent window
          const parentPaymentData = await new Promise((resolve) => {
            const timeout = setTimeout(() => resolve(null), 2000);
            
            const messageHandler = (event: MessageEvent) => {
              if (event.data && event.data.type === 'PAYMENT_DATA_RESPONSE') {
                clearTimeout(timeout);
                window.removeEventListener('message', messageHandler);
                resolve(event.data.paymentData);
              }
            };
            
            window.addEventListener('message', messageHandler);
            
            // Request payment data from parent
            sendMessageToParent({ type: 'REQUEST_PAYMENT_DATA' });
          });
          
          if (parentPaymentData && !iframePaymentData) {
            setIframePaymentData(parentPaymentData);
            console.log('PaymentSuccess - Retrieved payment data from parent window:', parentPaymentData);
          }
        } catch (error) {
          console.warn('PaymentSuccess - Failed to get payment data from parent:', error);
        }
      }
      
      if (status === 'successful' && txRef && transactionId) {
        // Payment was successful based on URL parameters
        // Get pending order data from localStorage
        const pendingOrderStr = localStorage.getItem('pendingOrder');
        console.log('PaymentSuccess - localStorage pendingOrder:', pendingOrderStr);
        
        if (pendingOrderStr) {
          const pendingOrder = JSON.parse(pendingOrderStr);
          console.log('PaymentSuccess - Parsed pendingOrder:', pendingOrder);
          
          // Convert CartItem to OrderItem and create order
          const orderItems = pendingOrder.items.map((item: CartItem) => ({
            type: item.type,
            id: item.id,
            itemId: item.itemId,
            name: item.name,
            price: item.price,
            amount: item.price, // For compatibility
            image: item.image
          }));
          
          const refBy = pendingOrder.referredBy ?? currentUser.referredBy ?? null;
          const order: Order = {
            id: `ORDER_${Date.now()}`,
            userId: currentUser.uid,
            items: orderItems,
            total: pendingOrder.total,
            currency: 'GHS',
            status: 'completed',
            paymentMethod: pendingOrder.paymentMethod,
            paymentReference: txRef,
            transactionId: transactionId,
            flutterwaveTransactionId: transactionId,
            ...(refBy ? { referredBy: refBy } : {}),
            shippingAddress: pendingOrder.customerInfo,
            createdAt: new Date(),
            updatedAt: new Date(),
            completedAt: new Date(),
          } as Order;
          
          // Save order to database
          const saveSuccess = await saveOrderToDatabase(order);
          
          if (saveSuccess) {
            // Delete online payment record since payment was successful
            try {
              await deleteOnlinePayment(txRef);
              console.log('Online payment record deleted successfully');
            } catch (error) {
              console.error('Failed to delete online payment record:', error);
              // Continue even if deletion fails
            }
            
            setOrderData(order);
            setPaymentStatus('success');
            
            // Clear cart and pending order
            clearCart();
            localStorage.removeItem('pendingOrder');
            
            // Send success message to parent window if in iframe
            if (isInIframe) {
              sendMessageToParent({ 
                type: 'PAYMENT_COMPLETED', 
                order: order,
                status: 'success'
              });
            }
            
            toast.success('Payment successful! Your order has been confirmed.');
          } else {
            setPaymentStatus('failed');
            setVerificationError('Payment successful but failed to save order. Please contact support.');
          }
        } else {
          console.log('PaymentSuccess - No pending order found in localStorage');
          setVerificationError('No pending order found. Please contact support.');
        }
      } else if (iframePaymentData) {
        // Handle iframe payment data
        console.log('PaymentSuccess - Processing iframe payment data:', iframePaymentData);
        
        const order: Order = {
          id: `ORDER_${Date.now()}`,
          userId: currentUser.uid,
          items: iframePaymentData.items || [],
          total: iframePaymentData.total || 0,
          currency: 'GHS',
          status: 'completed',
          paymentMethod: iframePaymentData.paymentMethod || 'online',
          paymentReference: iframePaymentData.paymentReference || 'IFRAME_PAYMENT',
          transactionId: iframePaymentData.transactionId || `IFRAME_${Date.now()}`,
          flutterwaveTransactionId: iframePaymentData.transactionId || `IFRAME_${Date.now()}`,
          ...(iframePaymentData.referredBy ? { referredBy: iframePaymentData.referredBy } : {}),
          shippingAddress: iframePaymentData.customerInfo || {},
          createdAt: new Date(),
          updatedAt: new Date(),
          completedAt: new Date(),
        } as Order;
        
        // Save order to database
        const saveSuccess = await saveOrderToDatabase(order);
        
        if (saveSuccess) {
          setOrderData(order);
          setPaymentStatus('success');
          
          // Clear cart and pending order
          clearCart();
          localStorage.removeItem('pendingOrder');
          
          // Send success message to parent window
          sendMessageToParent({ 
            type: 'PAYMENT_COMPLETED', 
            order: order,
            status: 'success'
          });
          
          toast.success('Payment successful! Your order has been confirmed.');
        } else {
          setPaymentStatus('failed');
          setVerificationError('Payment successful but failed to save order. Please contact support.');
        }
      } else {
        // Fallback to localStorage verification for other scenarios
        const pendingOrderStr = localStorage.getItem('pendingOrder');
        console.log('PaymentSuccess - Fallback localStorage pendingOrder:', pendingOrderStr);
        
        if (pendingOrderStr) {
          const pendingOrder = JSON.parse(pendingOrderStr);
          console.log('PaymentSuccess - Fallback parsed pendingOrder:', pendingOrder);
          
          // Convert CartItem to OrderItem and create order
          const orderItems = pendingOrder.items.map((item: CartItem) => ({
            type: item.type,
            id: item.id,
            itemId: item.itemId,
            name: item.name,
            price: item.price,
            amount: item.price, // For compatibility
            image: item.image
          }));
          
          const refBy2 = pendingOrder.referredBy ?? currentUser.referredBy ?? null;
          const order: Order = {
            id: `ORDER_${Date.now()}`,
            userId: currentUser.uid,
            items: orderItems,
            total: pendingOrder.total,
            currency: 'GHS',
            status: 'completed',
            paymentMethod: pendingOrder.paymentMethod,
            paymentReference: 'INLINE_PAYMENT',
            ...(refBy2 ? { referredBy: refBy2 } : {}),
            shippingAddress: pendingOrder.customerInfo,
            createdAt: new Date(),
            updatedAt: new Date(),
            completedAt: new Date(),
          } as Order;
          
          // Save order to database
          const saveSuccess = await saveOrderToDatabase(order);
          
          if (saveSuccess) {
            // Note: No online payment record to delete in fallback case
            // as this is for offline/inline payments
            
            setOrderData(order);
            setPaymentStatus('success');
            
            // Clear cart and pending order
            clearCart();
            localStorage.removeItem('pendingOrder');
            
            // Send success message to parent window if in iframe
            if (isInIframe) {
              sendMessageToParent({ 
                type: 'PAYMENT_COMPLETED', 
                order: order,
                status: 'success'
              });
            }
            
            toast.success('Payment successful! Your order has been confirmed.');
          } else {
            setPaymentStatus('failed');
            setVerificationError('Payment successful but failed to save order. Please contact support.');
          }
        } else {
          console.log('PaymentSuccess - No pending order found in fallback case');
          
          // If we're in an iframe and have no order data, try to get it from parent
          if (isInIframe) {
            console.log('PaymentSuccess - Attempting to get payment data from parent window...');
            
            // Send a final request to parent window
            sendMessageToParent({ 
              type: 'FINAL_PAYMENT_DATA_REQUEST',
              timestamp: Date.now()
            });
            
            // Wait a bit more for any delayed responses
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            // If still no data, show iframe-specific error
            if (!iframePaymentData) {
              setVerificationError('Payment data not available in iframe. Please contact support or try again.');
            }
          } else {
            setVerificationError('No pending order found');
          }
        }
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      setVerificationError('An error occurred while verifying payment');
    } finally {
      setIsVerifying(false);
    }
  }, [currentUser, clearCart, saveOrderToDatabase, isInIframe, sendMessageToParent]);

  useEffect(() => {
    if (!currentUser) {
      navigate('/signin');
      return;
    }

    verifyPayment();
  }, [currentUser, navigate]);

  const getStatusIcon = () => {
    switch (paymentStatus) {
      case 'success':
        return <CheckCircleIcon className="h-16 w-16 text-green-500" />;
      case 'failed':
        return <XCircleIcon className="h-16 w-16 text-red-500" />;
      case 'cancelled':
        return <XCircleIcon className="h-16 w-16 text-yellow-500" />;
      case 'pending':
        return <ClockIcon className="h-16 w-16 text-blue-500" />;
      default:
        return <ExclamationTriangleIcon className="h-16 w-16 text-gray-500" />;
    }
  };

  const getStatusTitle = () => {
    switch (paymentStatus) {
      case 'success':
        return 'Payment Successful!';
      case 'failed':
        return 'Payment Failed';
      case 'cancelled':
        return 'Payment Cancelled';
      case 'pending':
        return 'Payment Pending';
      default:
        return 'Payment Status Unknown';
    }
  };

  const getStatusMessage = () => {
    switch (paymentStatus) {
      case 'success':
        return 'Your payment has been processed successfully and your order has been confirmed.';
      case 'failed':
        return 'Your payment could not be processed. Please try again or contact support.';
      case 'cancelled':
        return 'You cancelled the payment process. No charges were made to your account.';
      case 'pending':
        return 'Your payment is being processed. Please wait for confirmation.';
      default:
        return 'We could not determine the status of your payment. Please contact support.';
    }
  };

  const getStatusColor = () => {
    switch (paymentStatus) {
      case 'success':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'failed':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'cancelled':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'pending':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 mb-4">Verifying payment...</p>
          
          {/* Payment Verification Warning */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-2">⚠️ Please Wait</p>
                <p>Do not close this page or browser while payment verification is in progress.</p>
                <p className="mt-1 text-xs">This process usually takes a few seconds.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Payment Status */}
        <div className="text-center mb-8">
          {getStatusIcon()}
          <h1 className="text-3xl font-bold text-gray-900 mt-4 mb-2">{getStatusTitle()}</h1>
          <p className="text-lg text-gray-600">{getStatusMessage()}</p>
          
          {verificationError && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg max-w-md mx-auto">
              <p className="text-sm text-red-600">{verificationError}</p>
            </div>
          )}
        </div>

        {/* Order Details - Only show if payment was successful */}
        {paymentStatus === 'success' && orderData && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Order Confirmation</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Order Information</h3>
                <div className="space-y-2 text-sm text-gray-600">
                  <p><span className="font-medium">Order ID:</span> {orderData.id}</p>
                  <p><span className="font-medium">Date:</span> {orderData.createdAt.toLocaleDateString()}</p>
                  <p><span className="font-medium">Total:</span> {new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(orderData.total)}</p>
                  <p><span className="font-medium">Payment Method:</span> {orderData.paymentMethod}</p>
                  <p><span className="font-medium">Status:</span> <span className="text-green-600 font-medium">Confirmed</span></p>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                  {shouldShowIOSWebViewAlert() ? "Items Added" : "Items Purchased"}
                </h3>
                <div className="space-y-2">
                  {orderData.items.map((item) => (
                    <div key={item.id} className="flex items-center space-x-3 p-2 bg-gray-50 rounded">
                      <div className="w-8 h-8 bg-primary-100 rounded flex items-center justify-center">
                        {item.type === 'course' && <BookOpenIcon className="h-4 w-4 text-primary-600" />}
                        {item.type === 'book' && <ShoppingBagIcon className="h-4 w-4 text-primary-600" />}
                        {item.type === 'video' && <BookOpenIcon className="h-4 w-4 text-primary-600" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{item.name}</p>
                        <p className="text-xs text-gray-500 capitalize">{item.type}</p>
                      </div>
                      <p className="text-sm font-semibold text-gray-900">
                        {new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(item.amount)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="text-sm font-medium text-green-800 mb-2">What's Next?</h4>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• You will receive a confirmation email shortly</li>
                <li>• For courses: Access will be granted immediately</li>
                <li>• For books: Download links will be sent to your email</li>
                <li>• Check your dashboard for all {shouldShowIOSWebViewAlert() ? "added" : "purchased"} items</li>
              </ul>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {paymentStatus === 'success' ? (
            <>
              <Link
                to="/app/dashboard"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <HomeIcon className="h-5 w-5 mr-2" />
                Go to Dashboard
              </Link>
              <Link
                to="/app/my-courses"
                className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <BookOpenIcon className="h-5 w-5 mr-2" />
                View My Courses
              </Link>
            </>
          ) : (
            <>
              <Link
                to="/app/checkout"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <ArrowRightIcon className="h-5 w-5 mr-2" />
                Try Again
              </Link>
              <Link
                to="/app/catalog"
                className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Browse Courses
              </Link>
            </>
          )}
        </div>

        {/* Support Information */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
            <span>Need help? Contact support at</span>
            <a href="mailto:profstrainingsolutions@gmail.com" className="ml-1 text-primary-600 hover:text-primary-500">
             profstrainingsolutions@gmail.com
            </a>
          </div>
        </div>

        {/* AI Chat Button */}
        <AIChatButton />
      </div>
    </div>
  );
};

export default PaymentSuccess;
